<ng-container *transloco="let t; read: 'admin.tenants'">
  <app-grid-page [title]="t('title')">
    <div class="flex h-full min-h-0 flex-col">
      <!-- KPI strip — cross-tenant totals from /admin/aggregates. px-4 because
           the page is full-bleed (grid-page); the grid below runs edge to edge. -->
      @let agg = aggregates();
      <div class="mb-5 grid flex-none grid-cols-2 gap-4 px-4 xl:grid-cols-4">
        <div class="rounded-card bg-card px-5 py-3.5 shadow-hairline">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-ink-3">{{ t('overview.stats.tenants') }}</div>
          <div class="mt-1.5 flex items-baseline gap-2.5">
            @if (isLoadingAggregates() && !agg) {
              <span class="grid-skeleton" aria-hidden="true"><span class="inline-block h-6 w-14 animate-pulse rounded bg-surface-2"></span></span>
            } @else {
              <span class="font-num text-[26px] font-semibold text-ink-1">{{ tenantsTotal() }}</span>
            }
            @if (agg) {
              <span class="text-[12px] text-ink-3">{{
                t('overview.stats.tenantsSub', { enabled: agg.tenants.enabled, disabled: agg.tenants.disabled })
              }}</span>
            }
          </div>
        </div>
        <div class="rounded-card bg-card px-5 py-3.5 shadow-hairline">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-ink-3">{{ t('overview.stats.users') }}</div>
          <div class="mt-1.5 flex items-baseline gap-2.5">
            @if (isLoadingAggregates() && !agg) {
              <span class="grid-skeleton" aria-hidden="true"><span class="inline-block h-6 w-14 animate-pulse rounded bg-surface-2"></span></span>
            } @else {
              <span class="font-num text-[26px] font-semibold text-ink-1">{{ agg?.users?.total ?? t('overview.dash') }}</span>
            }
            @if (agg) {
              <span class="text-[12px] text-ink-3">{{ t('overview.stats.usersSub') }}</span>
            }
          </div>
        </div>
        <div class="rounded-card bg-card px-5 py-3.5 shadow-hairline">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-ink-3">{{ t('overview.stats.invites') }}</div>
          <div class="mt-1.5 flex items-baseline gap-2.5">
            @if (isLoadingAggregates() && !agg) {
              <span class="grid-skeleton" aria-hidden="true"><span class="inline-block h-6 w-14 animate-pulse rounded bg-surface-2"></span></span>
            } @else {
              <span class="font-num text-[26px] font-semibold text-ink-1">{{ agg?.invitations?.pending ?? t('overview.dash') }}</span>
            }
            @if (agg?.invitations?.soonestExpiry; as expiry) {
              <span class="text-[12px] text-ink-3">{{ t('overview.stats.invitesSub', { date: expiry | date: 'mediumDate' }) }}</span>
            }
          </div>
        </div>
        <div class="rounded-card bg-card px-5 py-3.5 shadow-hairline">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-ink-3">{{ t('overview.stats.displays') }}</div>
          <div class="mt-1.5 flex items-baseline gap-2.5">
            @if (isLoadingAggregates() && !agg) {
              <span class="grid-skeleton" aria-hidden="true"><span class="inline-block h-6 w-14 animate-pulse rounded bg-surface-2"></span></span>
            } @else {
              <span class="font-num text-[26px] font-semibold text-ink-1">{{ agg?.displays?.total ?? t('overview.dash') }}</span>
            }
            @if (agg) {
              <span class="text-[12px] text-ink-3">{{ t('overview.stats.displaysSub', { online: agg.displays.online }) }}</span>
            }
          </div>
        </div>
      </div>

      <div class="min-h-0 flex-1">
        <app-grid
          [columns]="columns()"
          [data]="tenants()"
          [rowId]="tenantId"
          [loading]="isLoading()"
          [page]="page()"
          [pageSize]="size()"
          [totalElements]="totalElements()"
          [totalPages]="totalPages()"
          [sort]="sort()"
          [tabs]="statusTabs()"
          [activeTabId]="statusTab()"
          [searchable]="true"
          [rowActions]="rowActions()"
          [detailEnabled]="true"
          detailMode="overlay"
          [detailWidth]="380"
          [primaryAction]="{ label: t('add'), icon: 'icon-content-add24px' }"
          (sortChange)="onSortChange($event)"
          (pageChange)="onPageChange($event)"
          (pageSizeChange)="onPageSizeChange($event)"
          (searchChange)="onSearchChange($event)"
          (tabChange)="onTabChange($event)"
          (primaryActionClick)="openCreateDialog()"
          (rowActionClick)="onRowAction($event.actionId, asTenant($event.row))"
          (rowDblClick)="openDetail(asTenant($event))"
        >
          <!-- Detail drawer — opens on row click. -->
          <ng-template gridDetail let-row>
            @let tenant = asTenant(row);
            <div class="flex flex-col gap-4">
              <div class="grid grid-cols-[110px_1fr] items-center gap-x-3 gap-y-3 text-[14px] text-ink-1">
                <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('detail.alias') }}</span>
                <span class="truncate">{{ tenant.alias }}</span>
                <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('detail.status') }}</span>
                <app-label-badge [dot]="tenant.enabled ? 'green' : null">{{ tenant.enabled ? t('detail.enabled') : t('detail.disabled') }}</app-label-badge>
                @if (tenant.description) {
                  <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('detail.description') }}</span>
                  <span class="truncate">{{ tenant.description }}</span>
                }
              </div>
              <div class="h-px bg-hairline"></div>
              <div class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('detail.domains') }}</div>
              <div class="flex flex-col gap-2">
                @for (domain of tenant.domains ?? []; track domain.name) {
                  <div class="flex items-center justify-between gap-2 text-[14px] text-ink-1">
                    <span class="truncate">{{ domain.name }}</span>
                    <app-label-badge [dot]="domain.verified ? 'green' : null">{{ domain.verified ? t('detail.verified') : t('detail.unverified') }}</app-label-badge>
                  </div>
                } @empty {
                  <span class="text-[13px] text-ink-3">{{ t('detail.noDomains') }}</span>
                }
              </div>
              <div class="h-px bg-hairline"></div>
              <div class="break-all font-num text-[12px] text-ink-3">{{ t('detail.tenantId', { id: tenant.id }) }}</div>
              <div class="mt-2 flex flex-col gap-2.5">
                <app-button class="w-full" size="sm" iconLeft="icon-edit-pencil" [label]="t('detail.edit')" (activated)="onRowAction('edit', tenant)" />
                <app-button class="w-full" variant="primary" size="sm" destructive iconLeft="icon-delete-trash" [label]="t('detail.delete')" (activated)="onRowAction('delete', tenant)" />
              </div>
            </div>
          </ng-template>
        </app-grid>
      </div>
    </div>
  </app-grid-page>
</ng-container>

@if (isCreateDialogOpen()) {
  <app-create-tenant-dialog
    (close)="closeCreateDialog()"
    (save)="handleCreate($event)"
  ></app-create-tenant-dialog>
}

@if (editingTenantId(); as id) {
  <app-edit-tenant-dialog
    [tenantId]="id"
    (close)="closeEditDialog()"
    (saved)="handleUpdated()"
  ></app-edit-tenant-dialog>
}

@if (invitingTenant(); as tenant) {
  <app-invite-member-dialog
    [tenantId]="tenant.id"
    [tenantName]="tenant.name"
    (close)="closeInviteDialog()"
    (sent)="onInviteSent()"
  ></app-invite-member-dialog>
}
